import { useCallback, useEffect, useState } from 'preact/hooks'
import { subscribe } from '~/storage/invalidation'
import type { StoreName } from '~/storage/schema'

export type AsyncState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly value: T }
  | { readonly status: 'error'; readonly message: string }

export interface AsyncResult<T> {
  readonly state: AsyncState<T>
  readonly reload: () => void
}

/**
 * Load data from a repository, refetching when a relevant store is written.
 *
 * This is the whole of the state layer. D-025 rejected a reactive query library:
 * there is no network cache to reconcile, no request deduplication to do, and
 * the dataset is a few hundred rows. A load plus an invalidation subscription
 * covers it.
 *
 * `watch` names the stores whose writes should trigger a refetch.
 */
export function useAsyncData<T>(
  load: () => Promise<T>,
  watch: readonly StoreName[],
  deps: readonly unknown[] = [],
): AsyncResult<T> {
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' })
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => {
    setNonce((value) => value + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    // Keep the previous value on screen while refetching. Dropping back to
    // `loading` after every write would unmount the subtree, destroying local
    // form state — the "Saved." confirmation vanishes and the screen blinks.
    // Only the first load, or a genuine error, shows a loading state.
    setState((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }))

    load().then(
      (value) => {
        // A resolved load from a superseded effect must not overwrite newer state.
        if (!cancelled) setState({ status: 'ready', value })
      },
      (cause: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: cause instanceof Error ? cause.message : String(cause),
          })
        }
      },
    )

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line -- `load` is intentionally excluded; callers pass
    // an inline closure, and `deps` is the explicit dependency contract.
  }, [nonce, ...deps])

  useEffect(() => {
    if (watch.length === 0) return
    const watched = new Set(watch)
    return subscribe((changed) => {
      for (const store of changed) {
        if (watched.has(store)) {
          reload()
          return
        }
      }
    })
  }, [reload, watch])

  return { state, reload }
}
